import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { mitErsatz } from "@/office/makro/pruefung";
import { countWords, readDocument } from "@/office/word/document";
import { LESBARKEIT, readLesbarkeit } from "@/office/word/pruefen";
import { MAKRO_KORREKTUR } from "../makros";

export const lektionStatistik: Lesson = {
  id: "statistik",
  title: "Wörter zählen und Lesbarkeit messen",
  summary: "Wie lang ist der Text, wie lang sind die Sätze – und wie schwer liest er sich?",
  durationMinutes: 8,
  learningGoals: [
    "Wörter und Zeichen über die Statusleiste und „Wörter zählen“ ermitteln",
    "Die Lesbarkeitsstatistik einschalten und deuten",
    "Lange Sätze gezielt kürzen",
  ],
  steps: [
    {
      id: "woerter-zaehlen",
      title: "Wörter zählen",
      explanation: [
        "Die Statusleiste unten links zeigt die Wortzahl laufend an – bei markiertem Text " +
          "die der Markierung. „Überprüfen“ → „Wörter zählen“ nennt zusätzlich Zeichen mit " +
          "und ohne Leerzeichen, Absätze und Zeilen, auf Wunsch mit Fuß- und Endnoten.",
        "Vorgaben wie „höchstens 2.000 Zeichen inklusive Leerzeichen“ oder „300 Wörter“ " +
          "lassen sich so genau einhalten, ohne zu schätzen.",
      ],
      keyPoints: [
        "Statusleiste: Wortzahl laufend.",
        "Überprüfen → Wörter zählen für Zeichen mit/ohne Leerzeichen.",
      ],
      task: {
        instruction: "Schreibe einen zusammenhängenden Text von mindestens 120 Wörtern.",
        hint: "Die Statusleiste unten zeigt dir die aktuelle Wortzahl.",
        check: async () => {
          const woerter = countWords(await readDocument());
          return fromCriteria([criterion(`Mindestens 120 Wörter (gezählt: ${woerter})`, woerter >= 120)], {
            success: "Genug Text für eine aussagekräftige Lesbarkeitsstatistik.",
            failure: "Der Text ist noch zu kurz.",
          });
        },
      },
    },
    {
      id: "lesbarkeit",
      title: "Die Lesbarkeitsstatistik",
      explanation: [
        "Unter „Datei“ → „Optionen“ → „Dokumentprüfung“ → „Lesbarkeitsstatistik anzeigen“ " +
          "zeigt Word nach jeder vollständigen Rechtschreibprüfung (F7) eine Auswertung: " +
          "Wörter pro Satz, Zeichen pro Wort, Anteil der Passivsätze und den Lesbarkeitsindex " +
          "nach Flesch.",
        "Der Flesch-Wert reicht von 0 bis 100 – je höher, desto leichter lesbar. Fachtexte " +
          "liegen oft unter 30, Zeitungsartikel um 50 bis 60. Wichtiger als der Wert selbst " +
          "ist die Zahl der Wörter pro Satz: Über 20 wird es für die meisten Leser mühsam.",
        "Der wirksamste Hebel ist deshalb, lange Sätze zu teilen. Aus einem Satz mit drei " +
          "Nebensätzen werden zwei oder drei Hauptsätze – der Inhalt bleibt, der Text wird " +
          "leichter.",
      ],
      keyPoints: [
        "Optionen → Dokumentprüfung → Lesbarkeitsstatistik anzeigen, dann F7.",
        "Flesch: höher = leichter lesbar.",
        "Mehr als 20 Wörter pro Satz? Sätze teilen.",
      ],
      task: {
        instruction:
          "Überarbeite deinen Text so, dass er im Durchschnitt höchstens 20 Wörter pro Satz hat.",
        hint: "Lange Sätze am „und“ oder „weil“ teilen. Die Lesbarkeitsstatistik zeigt den Durchschnitt nach F7.",
        macro: MAKRO_KORREKTUR,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            async () => {
              const werte = await readLesbarkeit();
              return werte ? werte.map((eintrag) => eintrag.wert) : null;
            },
            MAKRO_KORREKTUR,
            (bericht) =>
              [...bericht.gruppe("lesbarkeit").values()].map((eintrag) => Number(eintrag.get("wert") ?? "NaN")),
            (werte) => {
              const proSatz = werte[LESBARKEIT.woerterProSatz];
              const woerter = werte[LESBARKEIT.woerter];
              if (proSatz === undefined || Number.isNaN(proSatz)) {
                return fail(
                  "Word hat keine Lesbarkeitsstatistik geliefert.",
                  "Die Statistik gibt es nur für Sprachen mit Grammatikprüfung. Prüfe, ob der Text als Deutsch gekennzeichnet ist.",
                );
              }
              return fromCriteria(
                [
                  criterion(`Mindestens 120 Wörter (laut Statistik: ${Math.round(woerter ?? 0)})`, (woerter ?? 0) >= 120),
                  criterion(`Höchstens 20 Wörter pro Satz (aktuell: ${proSatz.toFixed(1)})`, proSatz <= 20),
                ],
                {
                  success: "Kurze Sätze – der Text liest sich spürbar leichter.",
                  failure: "Die Sätze sind im Durchschnitt noch zu lang.",
                  hint: "Suche die längsten Sätze und teile sie.",
                },
              );
            },
          ),
      },
    },
  ],
};
